import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';

/**
 * Normaliza un teléfono a formato E.164 canónico (ej. "+5491122334455") para poder
 * comparar de forma determinista números guardados con formatos distintos (con/sin
 * código de país, con 0/15, con espacios). Es la base del lookup inverso
 * teléfono→establishment: identidad NO puede ser difusa, así que se normaliza con
 * libphonenumber, no con heurísticas ni con un LLM.
 *
 * @param phoneNumber - Teléfono en cualquier formato.
 * @param countryCode - País del comercio (ISO alpha-2) para desambiguar números sin
 *   código de país. 'XX' (desconocido) se ignora; un número ya internacional
 *   (con +/código de país) se parsea sin necesidad del país.
 * @returns E.164 con prefijo "+", o null si no es un número válido.
 */
export function toE164(
  phoneNumber: string | undefined | null,
  countryCode?: string,
): string | null {
  if (!phoneNumber) {
    return null;
  }
  const defaultCountry =
    countryCode && countryCode !== 'XX' ? (countryCode as CountryCode) : undefined;
  const parsed = parsePhoneNumberFromString(phoneNumber, defaultCountry);
  if (!parsed || !parsed.isValid()) {
    return null;
  }
  return parsed.number;
}

/**
 * En Argentina el `9` distingue móvil de fijo y el comercio puede haber cargado su
 * número sin él, pero WhatsApp SIEMPRE llega como móvil (+549...). Para que el lookup
 * inverso no dependa de cómo se cargó, ante un número AR guardamos ambas variantes del
 * mismo abonado (con y sin el 9). Para otros países no aplica (no tienen este quirk).
 */
export function withArMobileVariants(e164: string): string[] {
  if (!e164.startsWith('+54')) {
    return [e164];
  }
  if (e164.startsWith('+549')) {
    return [e164, `+54${e164.slice(4)}`];
  }
  return [e164, `+549${e164.slice(3)}`];
}

/**
 * Deriva el set único de teléfonos del titular en E.164, a partir de los campos del
 * establishment (phone principal, additionalPhone y contactInfo.phone). Es el valor
 * que se persiste en `ownerPhonesE164` para el lookup inverso por teléfono. Para AR
 * incluye las variantes móvil/fijo (ver {@link withArMobileVariants}).
 */
export function getOwnerPhonesE164(
  phones: {
    phone?: string;
    additionalPhone?: string;
    contactPhone?: string;
  },
  countryCode?: string,
): string[] {
  const unique = new Set<string>();
  for (const candidate of [phones.phone, phones.additionalPhone, phones.contactPhone]) {
    const e164 = toE164(candidate, countryCode);
    if (e164) {
      for (const variant of withArMobileVariants(e164)) {
        unique.add(variant);
      }
    }
  }
  return Array.from(unique);
}

const PLAUSIBLE_MIN_DIGITS = 8;
const PLAUSIBLE_MAX_DIGITS = 15;

/**
 * Valida que un teléfono tenga una cantidad de dígitos plausible.
 *
 * Deliberadamente NO aplica reglas por país. Formatos de uso corriente que libphonenumber rechaza
 * como número nacional —"15 2374107" en Argentina, "044 55…" en México— son los que la gente
 * realmente tipea en el checkout, y rechazarlos cuesta ventas. El bot no necesita el número
 * canónico acá: `orderOwnedByPhone` compara sólo dígitos y `normalizePhoneNumberForWhatsApp` (backend-kapso)
 * normaliza al enviar. Esto atrapa el typo real —faltan o sobran dígitos— y deja pasar el resto.
 */
export function isPlausiblePhoneNumber(phoneNumber: string | undefined | null): boolean {
  if (!phoneNumber) {
    return false;
  }

  const digits = phoneNumber.replace(/\D/g, '');

  return digits.length >= PLAUSIBLE_MIN_DIGITS && digits.length <= PLAUSIBLE_MAX_DIGITS;
}

/**
 * Normaliza un número de teléfono removiendo prefijos 549 o 54
 * También elimina el 0 inicial que puede aparecer después del código de país
 * @param phoneNumber - Número de teléfono a normalizar
 * @returns Número normalizado sin prefijos o null si no hay número
 */
export function normalizePhoneNumber(phoneNumber: string | undefined): string | null {
  if (!phoneNumber) {
    return null;
  }
  // Remover prefijos de país (549 o 54)
  let normalized = phoneNumber.replace(/^(549|54)/, '');
  
  // Si después de quitar el prefijo empieza con 0, eliminarlo
  // Esto maneja casos como "5403512374107" -> "03512374107" -> "3512374107"
  if (normalized.startsWith('0')) {
    normalized = normalized.substring(1);
  }
  
  return normalized;
}

/**
 * Obtiene una lista única de teléfonos con prefijo 549
 * Compara los números normalizados para evitar duplicados
 * @param mainPhone - Teléfono principal del establishment
 * @param contactPhone - Teléfono de contacto del establishment
 * @returns Array de teléfonos únicos con prefijo 549
 */
export function getUniquePhoneNumbers(
  mainPhone: string | undefined,
  contactPhone: string | undefined
): string[] {
  const normalizedMain = normalizePhoneNumber(mainPhone);
  const normalizedContact = normalizePhoneNumber(contactPhone);

  const uniquePhones = new Set<string>();

  if (normalizedMain) {
    uniquePhones.add(`549${normalizedMain}`);
  }

  if (normalizedContact && normalizedContact !== normalizedMain) {
    uniquePhones.add(`549${normalizedContact}`);
  }

  return Array.from(uniquePhones);
}

/**
 * Reduce el teléfono a solo dígitos, para usarlo como id estable de documento (ej. el
 * binding verificado teléfono↔establishment). No es E.164: no valida ni antepone "+".
 * Distinto de {@link normalizePhoneNumber}, que además remueve el prefijo de país AR.
 */
export function normalizePhone(phone: string): string {
  return phone.replace(/\D/g, '');
}

/**
 * Normaliza el teléfono de WhatsApp (X-Pedix-Phone / conversation.phone_number) a E.164.
 * Kapso lo manda en formato internacional con o sin "+"; se fuerza el "+" para que
 * libphonenumber lo parsee sin país. Es la clave del lookup inverso por teléfono contra
 * `ownerPhonesE164`, tanto en el MCP read-only como en la ingesta de conversaciones.
 */
export function whatsappPhoneToE164(phone: string | undefined): string | null {
  if (!phone?.trim()) {
    return null;
  }
  const trimmed = phone.trim();
  const candidate = trimmed.startsWith('+') ? trimmed : `+${normalizePhone(trimmed)}`;
  return toE164(candidate, undefined);
}

